<script setup lang="ts">
import {computed, ref, watch} from "vue";
import dayjs from "dayjs";
import {useNow} from "@vueuse/core";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import AgendaSlotGrid from "@/agenda/components/AgendaSlotGrid.vue";
import useOperatorChoices from "@/agenda/composables/useOperatorChoices";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {HealthJourneyStep} from "@/agenda/interfaces/healthJourney";

/**
 * Where to hold one act, chosen by hand.
 *
 * Two ways in: the moments the planner kept aside for this act, and the
 * agenda itself. The second is deliberately permissive — it shows what is
 * taken and who is habilitated, and lets the pharmacist decide anyway, because
 * they know things the planning does not. Whatever they freeze is sent back
 * to the planner as a pin, and the plan that comes back says plainly when a
 * frozen moment breaks a rule.
 */
const {step, actLabel} = defineProps<{
    step: HealthJourneyStep;
    actLabel: string;
}>();

const emits = defineEmits<{
    (e: 'pick', moment: {date: string; start: string; member_id: number | null}): void;
}>();

const open = defineModel<boolean>('open', {required: true});

/**
 * Followed rather than captured: a counter workstation stays on this screen
 * all day and through the night, and a « today » frozen at mount would open
 * the picker on yesterday.
 */
const now = useNow({interval: 60 * 1000});
const today = computed(() => dayjs(now.value).startOf('day'));

const day = ref(step.date ?? today.value.format('YYYY-MM-DD'));
const memberId = ref<number | null>(step.member_id);

// Reopened on another act, or on the same one after a recalculation: the
// picker starts again from where that act now stands.
watch(() => open.value, (isOpen) => {
    if (isOpen) {
        day.value = step.date ?? today.value.format('YYYY-MM-DD');
        memberId.value = step.member_id;
    }
});

const {choices} = useOperatorChoices(() => ({appointmentTypeId: step.appointment_type_id}));

const choose = (moment: {date: string; start: string; member_id: number | null}) => {
    emits('pick', moment);
    open.value = false;
};

const memberName = (id: number | null) =>
    id === null
        ? 'Sans collaborateur'
        : choices.value.find((choice) => choice.operator.accessId === id)?.operator.name ?? 'Collaborateur';
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
      <DialogHeader>
        <DialogTitle>Choisir un moment — {{ actLabel }}</DialogTitle>
        <DialogDescription>
          {{ step.duration_minutes }} minutes. Le parcours sera recalculé à partir de ce moment.
        </DialogDescription>
      </DialogHeader>

      <div class="min-h-0 flex-1 overflow-y-auto pr-1">
        <template v-if="step.alternatives.length">
          <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Autres moments proposés
          </p>
          <div class="mb-4 flex flex-col gap-1.5">
            <button
              v-for="alternative in step.alternatives"
              :key="`${alternative.date}-${alternative.start}`"
              type="button"
              class="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-primary-lightest"
              @click="choose(alternative)"
            >
              <span class="font-medium">
                {{ parseAgendaDate(alternative.date).format('dddd D MMMM') }} · {{ alternative.start }}
              </span>
              <span class="text-xs text-gray-500">{{ memberName(alternative.member_id) }}</span>
            </button>
          </div>
        </template>

        <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Choisir dans l'agenda
        </p>

        <AgendaSlotGrid
          v-model:day="day"
          v-model:member="memberId"
          :appointment-type-id="step.appointment_type_id"
          :duration-minutes="step.duration_minutes"
          :active="open"
          @pick="choose"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
